import { OptionsClass } from "./MainApp";
import { EstimationPrice, ShipmentId } from "./Booking";
import { STT } from "./CreateBookingV3";
import { DetailDiscount } from "./Tariff";
import { CountryData, DistrictData } from "./Location";

export const AddressTypeV4 = {
  HOME: "home",
  OFFICE: "office",
  APARTMENT: "apartment"
} as const;

export const BookingTabV4 = {
  DOMESTIC: "domestic",
  INTERNATIONAL: "international"
} as const;

export const ReceiptFormatV4 = {
  A6: "a6",
  A4: "a4",
  NONE: "none"
} as const;

export const PaymentMethodV4 = {
  CASH: "cash",
  COD: "cod",
  DFOD: "dfod",
  INVOICE_LIMIT: "pos_limit"
} as const;

export class SuccessBookingV4 {
  stt: STT[] = [];
  listDiscount: DetailDiscount[] = [];
  recipientName = "";
  destinationCityName = "";
  destinationDistrictName = "";
  estimateSla = "";
  estimationDate = "";
  codAmount = 0;
  codFee = 0;
  isCod = false;
  isDfod = false;
  shipmentId = "";
  paymentMethodLabel = "";

  constructor(fields?: Partial<SuccessBookingV4>) {
    Object.assign(this, fields);
  }
}

export const FormCompletionSectionKeyV4 = {
  SENDER: "sender",
  RECEIVER: "receiver",
  REFERENCE_NUMBER: "reference_number",
  GOODS_PIECES: "goods_pieces",
  PRODUCT_TYPE: "product_type",
  SUPPORTING_DOCUMENTS: "supporting_documents",
  PAYMENT_METHOD: "booking_v4_section_payment_method"
} as const;

export const ReferenceTypeV4 = {
  STT_MANUAL: "stt_manual",
  SHIPMENT_ID: "shipment_id",
  EXTERNAL_REF: "external_ref"
} as const;

export type BookingStatus =
  | "belum_dibooking"
  | "berhasil_dibooking"
  | "gagal_dibooking";

export class ReferenceChipV4 {
  value = "";
  type = "";
  status: "idle" | "loading" | "valid" | "invalid" = "idle";
  // Per-chip validation error text (GENESIS-19949) — lets the aggregate
  // errors.referenceNumber be derived from current chip state instead of
  // whichever async validation call happens to resolve last.
  errorMessage = "";

  constructor(fields?: Partial<ReferenceChipV4>) {
    Object.assign(this, fields);
  }
}

export const FormCompletionSectionStateV4 = {
  INCOMPLETE: "incomplete",
  COMPLETE: "complete"
} as const;

// --- Page meta (GENESIS-19568) ---
export class PageMetaBookingV4 {
  activeTab: typeof BookingTabV4[keyof typeof BookingTabV4] =
    BookingTabV4.DOMESTIC;
  receiptFormat: typeof ReceiptFormatV4[keyof typeof ReceiptFormatV4] =
    ReceiptFormatV4.A6;

  constructor(fields?: Partial<PageMetaBookingV4>) {
    Object.assign(this, fields);
  }
}

// --- Pengirim (GENESIS-19570) ---
export class SenderBookingV4 {
  id = 0;
  name = "";
  phoneFlag = "";
  phoneCode = "+62";
  phone = "";
  address = "";
  isSaveCustomer = false;
  clientId = "";
  clientName = "";
  clientCode = "";
  clientAlias = "";
  clientOriginCity = "";
  clientCategory = "";
  clientPackageType = "";
  clientCountryCode = "";
  originCity: string | DistrictData = "";
  originCountryCode = "";
  originCountry = new CountryData();

  constructor(fields?: Partial<SenderBookingV4>) {
    Object.assign(this, fields);
  }
}

// --- Penerima (GENESIS-19570) ---
export class ReceiverBookingV4 {
  id = 0;
  name = "";
  phoneFlag = "";
  phoneCode = "+62";
  phone = "";
  address = "";
  addressType = new OptionsClass({ value: AddressTypeV4.HOME });
  destinationCity: string | DistrictData = "";
  isVendorNinjaDestination = false;
  postalCode: string | DistrictData = "";
  destinationCountryCode = "";
  destinationCountry = new CountryData();
  isSaveCustomer = false;

  constructor(fields?: Partial<ReceiverBookingV4>) {
    Object.assign(this, fields);
  }
}

// --- Shared value objects (GENESIS-19570) ---
export interface CountryInfoV4 {
  code: string;
  name: string;
  flagIconUrl: string;
}

// --- Barang & Komoditas (GENESIS-19571) ---
export class CommodityBookingV4 {
  id = 0;
  name = "";
  // English name (commodity_name_en) — used when isForeign is true (PRD §3.7.3).
  nameEn = "";
  code = "";
  hsCode = "";
  categoryCode = "";
  isDangerousGoods = false;
  isQuarantine = false;
  status = "";
  isNotFound = false;

  constructor(fields?: Partial<CommodityBookingV4>) {
    Object.assign(this, fields);
  }
}

// --- Kategori komoditas modal Lihat Semua (GENESIS-19985) ---
export interface CommodityCategoryV4 {
  code: string;
  name: string;
  nameEn?: string;
}

// --- Paket tersimpan pelanggan (GENESIS-20970) ---
// Card paket tersimpan sender (Saved Package Cards). `commodityName` /
// `isDangerousGoods` / `isQuarantine` di-resolve dari commodity master
// (getDetailCommodityByCode), bukan dari response saved-package.
export class SavedPackagePieceBookingV4 {
  grossWeight = 0;
  length = 0;
  width = 0;
  height = 0;

  constructor(fields?: Partial<SavedPackagePieceBookingV4>) {
    Object.assign(this, fields);
  }
}

export class SavedPackageBookingV4 {
  id = 0;
  commodityCode = "";
  commodityId = 0;
  commodityStatus = "";
  totalPieces = 0;
  totalGrossWeight = 0;
  source = "";
  commodityName = "";
  commodityNameEn = "";
  isDangerousGoods = false;
  isQuarantine = false;
  pieces: SavedPackagePieceBookingV4[] = [];

  constructor(fields?: Partial<SavedPackageBookingV4>) {
    Object.assign(this, fields);
  }
}

export class ErrorPiecesBookingV4 {
  errorHws = false;
  errorMaximumGrossWeight = false;
  errorMandatoryGrossWeight = false;
  errorMandatoryVolumeWeight = false;
  errorDecreasedWeight = false;
  constructor(fields?: Partial<ErrorPiecesBookingV4>) {
    Object.assign(this, fields);
  }
}

export class PieceBookingV4 {
  length = 0;
  width = 0;
  height = 0;
  grossWeight = 0;
  volumeWeight = 0;
  error: ErrorPiecesBookingV4 = new ErrorPiecesBookingV4();

  constructor(fields?: Partial<PieceBookingV4>) {
    Object.assign(this, fields);
  }
}

// --- Heavyweight Surcharge threshold config (GENESIS-19986) ---
export class HwsThresholdConfig {
  minimumWeightKg = 0;
  message = "";
  minimumExist = false;

  constructor(fields?: Partial<HwsThresholdConfig>) {
    Object.assign(this, fields);
  }
}

export class CommodityCiplBookingV4 {
  id = 0;
  name = "";
  quantity = "";
  goodsPrice = "";
  commodityId = 0;
  code = "";
  hsCode = "";
  isDangerousGoods = false;
  isQuarantine = false;
  status = "";

  constructor(fields?: Partial<CommodityCiplBookingV4>) {
    Object.assign(this, fields);
  }
}

// --- Jenis Pengiriman (GENESIS-19573) ---
/** View DTO — satu baris kartu upselling; tidak disimpan di CreateBookingV4 */
export interface ProductCardItem {
  productType: string;
  isAvailable: boolean;
  isEmbargo: boolean;
  isRecommended: boolean;
  shippingCost: number | null;
  slaMinDays: number;
  slaMaxDays: number;
  commissionRate: number;
  commissionAmount: number;
  showMinWeightNote?: boolean;
}

export class ProductBookingV4 {
  productType = "";
  isVippack = false;
  estimateSla = "";
  totalTariffAfterDiscount = 0;
  bookingCommission = 0;
  commissionRate = 0;
  isEmbargo = false;

  constructor(fields?: Partial<ProductBookingV4>) {
    Object.assign(this, fields);
  }
}

// --- Metode Pembayaran (GENESIS-19576, GENESIS-19584) ---
export class PaymentMethodBookingV4 {
  method: typeof PaymentMethodV4[keyof typeof PaymentMethodV4] =
    PaymentMethodV4.CASH;
  codAmount: number | null = null;

  constructor(fields?: Partial<PaymentMethodBookingV4>) {
    Object.assign(this, fields);
  }
}

// --- Opsi metode pembayaran per tombol selector (GENESIS-19576) ---
export class PaymentMethodOptionV4 {
  key: typeof PaymentMethodV4[keyof typeof PaymentMethodV4] =
    PaymentMethodV4.CASH;
  disabled = false;
  hoverCopy = "";
  warningCopy = "";
  remainingLimit: number | null = null; // hanya invoice_limit; format di child

  constructor(fields?: Partial<PaymentMethodOptionV4>) {
    Object.assign(this, fields);
  }
}

// --- Protection, item price, wood packing, CW (GENESIS-19577) ---
export class ProtectionPackingBookingV4 {
  protectionChecked = false;
  protectionDisabled = false;
  protectionLabel = ""; // label UI via i18n
  itemPrice: number | null = null;
  woodpacking = false;
  woodpackingEligible = false;
  woodpackingAdditionalWeightKg = 0;
  chargeableWeightKg = 0;
  chargeableWeightExpanded = false;
  totalKoli = 0;
  insuranceType = "free";
  hasChanged = false;
  lastChecked = false; // last checked state (for diffing on submit)
  showTresholdProtection = false;
  changeType: "woodpacking" | "" = "";

  constructor(fields?: Partial<ProtectionPackingBookingV4>) {
    Object.assign(this, fields);
  }
}

// --- Dokumen Pendukung (GENESIS-19572) ---
export type DutyTaxTermsBookingV4 = "DAP" | "DDP";

export class DocInternationalBookingV4 {
  listCommodityCipl: CommodityCiplBookingV4[] = [];
  email = "";
  idNumber = "";
  taxNumber = "";
  imageBeforePackingUrl = "";
  imageAfterPackingUrl = "";
  imageIdUrl = "";
  imageTaxUrl = "";
  shipmentStatus = new OptionsClass();
  // Pembayaran Bea Cukai & Pajak (PRD §3.9.5) — hanya Internasional (isFtz=false).
  dutyTaxTerms: DutyTaxTermsBookingV4 | null = null;

  constructor(fields?: Partial<DocInternationalBookingV4>) {
    Object.assign(this, fields);
  }
}

export class AdditionalDocumentBookingV4 {
  id = "";
  url = "";
  fileName = "";
  fileSize = 0;

  constructor(fields?: Partial<AdditionalDocumentBookingV4>) {
    Object.assign(this, fields);
  }
}

export class SupportingDocBookingV4 {
  docInternational = new DocInternationalBookingV4();
  ftzForm = new DocInternationalBookingV4();
  // Tambah Dokumen Pendukung Lain — net-new, maks 5 (Intl & FTZ).
  additionalDocuments: AdditionalDocumentBookingV4[] = [];

  constructor(fields?: Partial<SupportingDocBookingV4>) {
    Object.assign(this, fields);
  }
}

// --- Voucher & OTP (GENESIS-19579, GENESIS-19592) ---
export class VoucherBookingV4 {
  appliedVoucherCode = "";
  appliedVoucherId = 0;
  appliedVoucherCodeId = 0;
  otpToken = "";
  otpCode = "";
  goberReservationId = "";
  bookingAttemptId = "";

  constructor(fields?: Partial<VoucherBookingV4>) {
    Object.assign(this, fields);
  }
}

// --- Form completion / Kelengkapan Pengisian (GENESIS-19575, GENESIS-19583) ---
// Tipe shape item panel — BUKAN field mutable di CreateBookingV4.
// Panel `FormCompletionPanel` menerima `items` dari getter `formCompletionItems` pada parent (logic inline — GENESIS-19583).
export class FormCompletionSectionBookingV4 {
  // Explicitly annotated (not inferred) so consumers can compare against the
  // full FormCompletionSectionKeyV4 union — an unannotated initializer here
  // infers the single literal type of the default value, which breaks
  // strict-mode comparisons like `item.key === OTHER_KEY` (TS2367).
  key: typeof FormCompletionSectionKeyV4[keyof typeof FormCompletionSectionKeyV4] =
    FormCompletionSectionKeyV4.SENDER;
  label = ""; // label UI via i18n
  // Widened to `string` (not the 2-value union) on purpose: the domain enum
  // stays complete|incomplete per detailing, but styling for a section with
  // invalid fields is driven by an "error" value the parent getter
  // (GENESIS-19583) may thread through without a formal enum entry.
  state: string = FormCompletionSectionStateV4.INCOMPLETE;

  constructor(fields?: Partial<FormCompletionSectionBookingV4>) {
    Object.assign(this, fields);
  }
}

// --- Form completion panel — rincian Dokumen Pendukung (GENESIS-19575) ---
export class FormCompletionFieldRowV4 {
  fieldKey = "";
  fieldLabel = "";
  status: "wajib" | "opsional" = "wajib";

  constructor(fields?: Partial<FormCompletionFieldRowV4>) {
    Object.assign(this, fields);
  }
}

// Item panel = domain section + optional sub-list Dokumen Pendukung (UI-only extension).
export type FormCompletionPanelItemV4 = FormCompletionSectionBookingV4 & {
  fields?: FormCompletionFieldRowV4[]; // hanya item key supporting_documents
};

// --- Riwayat booking POS (GENESIS-19569) — page state, bukan bagian payload submit ---
export class BookingHistoryItemBookingV4 {
  sttNo = "";
  isCod = false;
  isDfod = false;
  senderName = "";
  productType = "";
  chargeableWeight = 0;
  totalTariff = 0;
  isReceiptPrinted = false;
  isInvoiceCreated = false;

  constructor(fields?: Partial<BookingHistoryItemBookingV4>) {
    Object.assign(this, fields);
  }
}

// --- Error state entity v4 ---
export class ErrorBookingV4 {
  originCountry = "";
  sender = "";
  phoneSender = "";
  addressSender = "";
  receiver = "";
  phoneReceiver = "";
  addressReceiver = "";
  destinationCity = "";
  postalCode = "";
  destinationCountry = "";
  externalReference = "";
  referenceNumber = ""; // aggregate error Nomor Referensi chips — GENESIS-19949
  commodity = "";
  itemPrice = "";
  pieces = "";
  supportingDoc = "";
  voucher = "";

  constructor(fields?: Partial<ErrorBookingV4>) {
    Object.assign(this, fields);
  }
}

// --- Chargeable weight breakdown dropdown (GENESIS-19578) ---
export class ChargeableWeightBreakdownV4 {
  totalGrossWeightKg = 0;
  totalVolumeWeightKg = 0;
  totalWpWeightKg = 0;
  finalCwKg = 0;

  constructor(fields?: Partial<ChargeableWeightBreakdownV4>) {
    Object.assign(this, fields);
  }
}

// --- Exchange rate Est. CIPL §3.6.7 (GENESIS-19589) ---
export class ExchangeRateBookingV4 {
  exchangeRateId = 0;
  exchangeRateFrom = "";
  exchangeRateTo = "";
  exchangeRate = 0;
  exchangeRateStatus = "";
  startDate = "";
  endDate = "";

  constructor(fields?: Partial<ExchangeRateBookingV4>) {
    Object.assign(this, fields);
  }
}

// --- Agregat form create v4 ---
export class CreateBookingV4 {
  pageMeta = new PageMetaBookingV4();
  sender = new SenderBookingV4();
  receiver = new ReceiverBookingV4();
  references: ReferenceChipV4[] = []; // Nomor Referensi chips — GENESIS-19949
  isReferenceRequired = false; // Retail default opsional; independen dari Client DO
  commodity = new CommodityBookingV4();
  pieces: PieceBookingV4[] = [];
  commodityCipl: CommodityCiplBookingV4[] = [];
  product = new ProductBookingV4();
  paymentMethod = new PaymentMethodBookingV4();
  protectionPacking = new ProtectionPackingBookingV4();
  supportingDoc = new SupportingDocBookingV4();
  voucher = new VoucherBookingV4();
  estimationPrice = new EstimationPrice(); // reuse entity existing (GENESIS-19578, GENESIS-19591)
  bookingHistory: BookingHistoryItemBookingV4[] = [];
  shipmentId = "";
  adjustmentWeightAttachments: string[] = [];

  constructor(fields?: Partial<CreateBookingV4>) {
    Object.assign(this, fields);
  }
}

export class CountryOption {
  flag = "";
  code = "";
  name = "";
  nationalPrefix = "";
  countryCode = "";

  constructor(fields?: Partial<CountryOption>) {
    Object.assign(this, fields);
  }
}

export class FailedBookingData {
  error = "";
  balance = 0;
  price = 0;

  constructor(fields?: Partial<FailedBookingData>) {
    Object.assign(this, fields);
  }
}

export class LogProsesScan {
  id = "";
  shipmentNumber = "";
  reasonFailed = "";
  scannedAt = "";

  constructor(fields?: Partial<LogProsesScan>) {
    Object.assign(this, fields);
  }
}

export class ShipmentIdTable extends ShipmentId {
  id = 0;
  no = 0;
  isDeleted = false;
  isInstant = false;
  isLoading = false;
  error: null | string = null;
  bookingStatus: BookingStatus = "belum_dibooking";
  groupId = "";

  constructor(fields?: Partial<ShipmentIdTable>) {
    super();
    Object.assign(this, fields);
  }
}
